/// <amd-module name="Wiki/Overview/Actions/ActionCreatorsHub" />
import * as Utils_String from "VSS/Core/Util/String";
import { ILegacyPlatformService } from "VSS/Legacy/Legacy";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { FastPageSwitch, onClickFPS } from "VSS/Platform/FPS";
import { StateMergeOptions } from "VSS/Platform/History";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import * as VCContracts from "Wiki/Clients/VC/Contracts/Git";
import { WikiPage, WikiV2 } from "Wiki/Clients/Wiki/Contracts/Wiki";
import { IEditorActionCreator, IWikiEditorService } from "Wiki/Editor/Contracts";
import { IHeaderMetaDataService } from "Wiki/HeaderMetaData/Interfaces/IHeaderMetadataService";
import { IHeaderMetaDataActionCreator } from "Wiki/HeaderMetaData/Interfaces/IHeaderMetadataTypes";
import { PageTreeActionCreator } from "Wiki/Overview/Actions/PageTreeActionCreator";
import * as Resources from "Wiki/Overview/Resources";
import { WikiPageSource } from "Wiki/Overview/Sources/WikiPageSource";
import { OverviewActions, StoresHub } from "Wiki/Overview/Stores/StoresHub";
import { c_vcPreviewModeActionId, getExplorerUrl, openUrlInNewTab } from "Wiki/Overview/Utils/UrlHelper";
import { getPageInfoWithName, ingestPageView, isFolderAssociatedPage } from "Wiki/Overview/Utils/WikiHelper";
import { WikiPageSwitchTtiLogger } from "Wiki/Overview/Utils/WikiPageSwitchTtiLogger";
import { wikiFilteredPageClicked } from "Wiki/Overview/Utils/WikiTelemetryHelper";
import { ActionIds, RepoConstants } from "Wiki/ViewCommon/Constants";
import {
    IEditorLegacyModules,
    IWikiMentionNwpWrapperModule,
    UrlParameters,
    WikiPageInfo,
    WikiPageInfoWithName,
    WikiViewOptions
} from "Wiki/ViewCommon/Contracts";
import { PerformanceConstants, TelemetryConstants } from "Wiki/ViewCommon/CustomerIntelligenceConstants";
import { IStore } from "Wiki/ViewCommon/IStore";
import { getGitItemPath, getParentPath } from "Wiki/ViewCommon/Utils/PathHelper";
import {
    getPagePathBasedUrlAndState,
    getVersionFromBranchName,
    getWikiUrl,
    isSimpleUrlEnabled,
    publish,
    setWindowTitle,
    updateToPagePathBasedUrlIfNeeded,
    updateUrl
} from "Wiki/ViewCommon/Utils/WikiHelper";

export const PrintPageClickedEvent = "printWikiPageClicked";
export interface Sources {
    pageSource: WikiPageSource;
}

export const NavPaneCollapseThreshold = 32;

export class ActionCreatorsHub {
    public get treeAC(): PageTreeActionCreator {
        return this._treeActionCreator;
    }
    private _asyncEditorAC: IEditorActionCreator | undefined;
    private _treeActionCreator: PageTreeActionCreator;
    private _asyncPageMetaDataAC: IHeaderMetaDataActionCreator | undefined;
    private _registerEditorFluxPromise: Promise<IEditorLegacyModules> | undefined;

    constructor(
        private _pageContext: IVssPageContext,
        private _wiki: WikiV2,
        private _version: VCContracts.GitVersionDescriptor,
        private _repository: VCContracts.GitRepository,
        private _sources: Sources,
        private _overviewActions: OverviewActions,
        private _storesHub: StoresHub,
        private _addStoreListener: (store: IStore) => void,
        wikiViewOptions: WikiViewOptions,
        wikiPageSwitchTtiLogger: WikiPageSwitchTtiLogger,
        isEmptyWiki: boolean,
        treeActionCreator?: PageTreeActionCreator // passing optional parameter for UT's
    ) {
        this._treeActionCreator = treeActionCreator
            ? treeActionCreator
            : new PageTreeActionCreator(
                  this._pageContext,
                  this._wiki,
                  this._version,
                  this._sources.pageSource,
                  this._overviewActions,
                  this._storesHub,
                  wikiPageSwitchTtiLogger,
                  this._loadHeaderMetadata
              );

        if (wikiViewOptions.action === ActionIds.Edit) {
            const pageInfo = this._storesHub.stores.overviewStore.getState().pageInfo;
            if (pageInfo) {
                this.startEditing(pageInfo.path, true);
            }
        }

        if (isEmptyWiki) {
            this.startEditingZeroDay();
        }
    }

    public getEditorAC(): IEditorActionCreator | undefined {
        return this._asyncEditorAC;
    }

    public getHeaderMetaDataActionCreator(): IHeaderMetaDataActionCreator | undefined {
        return this._asyncPageMetaDataAC;
    }

    public onNavPaneResize = (newSize: number): void => {
        const NavPaneMinimumSize = 200;
        // 1) if the user has dropped the splitter at the very start treat it as collapsed
        // 2) don't allow him to resize less than min size, the reason being, it is useless to have a small nav pane
        if (newSize <= NavPaneCollapseThreshold) {
            this._overviewActions.navPaneVisibilityChanged.invoke(true);
        } else if (newSize <= NavPaneMinimumSize) {
            this._overviewActions.navPaneResized.invoke(NavPaneMinimumSize);
        } else {
            this._overviewActions.navPaneResized.invoke(newSize);
        }
    };

    public onToggleNavPaneVisibility = (): void => {
        const navPaneState = this._storesHub.stores.overviewStore.getState().navPaneState;
        this._overviewActions.navPaneVisibilityChanged.invoke(!navPaneState.isCollapsed);
    };

    public loadHeaderMetadata(): void {
        const currentPageInfo = this._storesHub.stores.overviewStore.getState().pageInfo;
        if (currentPageInfo) {
            this._loadHeaderMetadata(currentPageInfo.path);
        }
    }

    public onPageDelete = (pagePath: string): void => {
        this._overviewActions.pageDeleted.invoke({ deletedPagePath: pagePath });

        const nextWikiTreePageToSelect = this._getNextPageToSelect(this._getParentPageOrHomePagePath(pagePath));
        if (nextWikiTreePageToSelect) {
            this.onPageSelected(nextWikiTreePageToSelect, true);
        } else {
            this.startEditingZeroDay();
        }

        this.treeAC.onFilteringDataSetChanged();
    };

    public startEditingNewPage = (initialPageTitle: string = ""): void => {
        const currentPageInfo = this._storesHub.stores.overviewStore.getState().pageInfo;
        const parentpage = currentPageInfo ? getParentPath(currentPageInfo.path) : RepoConstants.RootPath;
        const mode = this._storesHub.stores.overviewStore.getState().isEditing ? TelemetryConstants.EditMode : TelemetryConstants.ViewMode;
        publish(this._pageContext, TelemetryConstants.NewPageButtonClicked, this._wiki.type, { mode });
        this._startEditingEmptyPage(parentpage, false, initialPageTitle);
    };

    public startEditingZeroDay = (): void => {
        this._startEditingEmptyPage(RepoConstants.RootPath, true);
    };

    public addSubPageToCurrentPage = (): void => {
        const currentPageInfo = this._storesHub.getState().overviewState.pageInfo;
        currentPageInfo && this.addSubPage(currentPageInfo.path);
    };

    public addSubPage = (parentPath: string) => {
        this._startEditingEmptyPage(parentPath);
    };

    public startEditingCurrentPage = (): void => {
        const currentPageInfo = this._storesHub.getState().overviewState.pageInfo;
        currentPageInfo && this.startEditing(currentPageInfo.path);
    };

    public startEditing = (pagePath: string, skipUpdateUrl?: boolean): void => {
        this._warnBeforePageSwitch(() => {
            const currentPageInfo = this._storesHub.stores.overviewStore.getState().pageInfo;
            const isEditingLoadedPage = (currentPageInfo && pagePath === currentPageInfo.path) || false;
            this._logPageEditAndStartScenario(false, isEditingLoadedPage);
            if (isEditingLoadedPage) {
                if (!isSimpleUrlEnabled(this._pageContext)) {
                    this._updateUrlIfNeeded({ _a: ActionIds.Edit }, undefined, skipUpdateUrl);
                } else {
                    // since we support simple URL only for view, force it to be path based template on other places like revision, edit..etc
                    // else by default it would pick simple URL format which doesnt have page path and so doesnt work
                    this._updateToPagePathBasedUrlIfNeeded(
                        { _a: ActionIds.Edit, pagePath: pagePath, pageId: currentPageInfo && currentPageInfo.id },
                        skipUpdateUrl
                    );
                }
                this._overviewActions.startEditingCurrentPage.invoke();
                this._initializeEditor(getPageInfoWithName(currentPageInfo!)); // if currentPageInfo is undefined, isEditingLoadedPage will be false.
            } else {
                this._sources.pageSource.getPageInfo(this._wiki, pagePath, this._version).then((wikiPageInfo: WikiPageInfo) => {
                    if (!isSimpleUrlEnabled(this._pageContext)) {
                        this._updateUrlIfNeeded(
                            {
                                pageId: wikiPageInfo.id,
                                pagePath: wikiPageInfo.path,
                                _a: ActionIds.Edit
                            },
                            undefined,
                            skipUpdateUrl
                        );
                    } else {
                        this._updateToPagePathBasedUrlIfNeeded(
                            { _a: ActionIds.Edit, pageId: wikiPageInfo.id, pagePath: wikiPageInfo.path },
                            skipUpdateUrl
                        );
                    }

                    this._overviewActions.startEditing.invoke(wikiPageInfo);
                    this._initializeEditor(getPageInfoWithName(wikiPageInfo));
                    this._loadHeaderMetadata(pagePath);
                });
            }
        }, skipUpdateUrl);
    };

    public redirectToCode = (pagePath: string): void => {
        const gitPath = getGitItemPath(pagePath, this._wiki.mappedPath);
        const version = getVersionFromBranchName(this._version.version);
        const url = getExplorerUrl(this._pageContext, this._repository.project.name, this._repository.name, gitPath, c_vcPreviewModeActionId, {
            version
        });

        onClickFPS(this._pageContext, url, true);
    };

    public openInNewTab = (pagePath: string): void => {
        openUrlInNewTab(
            getPagePathBasedUrlAndState(
                this._pageContext,
                {
                    pagePath
                },
                { wikiIdentifier: this._wiki.name, wikiVersion: getVersionFromBranchName(this._version.version) }
            ).url
        );
    };

    public closeEditor = (skipUpdateUrl?: boolean): void => {
        this._updateUrlIfNeeded({ _a: ActionIds.View, anchor: undefined }, undefined, skipUpdateUrl);
        this._overviewActions.closeEditor.invoke(undefined);
        const overviewState = this._storesHub.stores.overviewStore.getState();
        if (overviewState.pageInfo) {
            ingestPageView(this._pageContext, this._wiki.id, overviewState.pageInfo.id, overviewState.pageInfo.path);
        }
    };

    public onPageSelected = (newPage?: WikiPage, updateTree?: boolean, skipUpdateUrl?: boolean, anchor?: string, forcePageSelect?: boolean): void => {
        const overviewState = this._storesHub.stores.overviewStore.getState();
        if (!forcePageSelect && overviewState.pageInfo && newPage && overviewState.pageInfo.path === newPage.path) {
            this.treeAC.onPageToggled(newPage); // if user clicks on already selected page, just collapse it
            return;
        }
        this._warnBeforePageSwitch(() => {
            this._onPageSelected(newPage, updateTree, skipUpdateUrl, anchor);
        }, skipUpdateUrl);
    };

    public removeAnchor = (skipUpdateUrl?: boolean): void => {
        const currentPageInfo = this._storesHub.stores.overviewStore.getState().pageInfo;
        // we need to pass pagepath as that may not be in state as we don't store page path in URL with simple Url
        this._updateUrlIfNeeded({ anchor: undefined, pagePath: currentPageInfo && currentPageInfo.path }, undefined, skipUpdateUrl);
        this._overviewActions.anchorRemoved.invoke(undefined);
    };

    public onAnchorClicked = (anchor: string, skipUpdateUrl?: boolean): void => {
        const currentPageInfo = this._storesHub.stores.overviewStore.getState().pageInfo;
        this._updateUrlIfNeeded({ anchor, pagePath: currentPageInfo && currentPageInfo.path }, undefined, skipUpdateUrl);
        this._overviewActions.anchorClicked.invoke(anchor);
    };

    public onFilteredPageSelected = (newPage: WikiPage): void => {
        wikiFilteredPageClicked(this._pageContext, this._wiki.type);
        this.onPageSelected(newPage);
    };

    public updateLinkedWit = (witCount: number): void => {
        this._overviewActions.linkedWitUpdated.invoke(witCount);
    };

    /**
     * On wit embed failure show the error banner
     */
    public handleEmbedWorkItemFailure = (errorMessage: string): void => {
        this._overviewActions.onEmbedWitFailed.invoke({
            message: errorMessage,
            buttonProps: [
                {
                    text: Resources.PageLearnMoreText,
                    ariaLabel: Resources.CreateAndEmbedWITFailedLearnMoreAriaLabel,
                    href: Resources.CreateAndEmbedWITLearnMoreLink,
                    target: "_blank"
                }
            ]
        });
    };

    /**
     * This will save the updated content and do FPS
     */
    public handleEmbedWorkItem = async (content: string, workItemId: number, workItemType: string, errorMessage: string): Promise<void> => {
        const overviewState = this._storesHub.stores.overviewStore.getState();
        if (overviewState.pageInfo) {
            if (overviewState.pageInfo.version === undefined) {
                if ($DEBUG) {
                    throw new Error("attempting to save page without etag");
                }
                return Promise.reject();
            }
            try {
                const commitMsg = Utils_String.format(Resources.CreatedAndEmbeddedWit, workItemType, workItemId);
                await this._sources.pageSource.createOrUpdatePage(
                    this._wiki,
                    overviewState.pageInfo.path,
                    content,
                    overviewState.pageInfo.version,
                    commitMsg,
                    this._version
                );
                const currentWikiUrl = getWikiUrl(
                    this._pageContext,
                    { pagePath: overviewState.pageInfo.path, pageId: overviewState.pageInfo.id },
                    StateMergeOptions.routeValues
                );
                FastPageSwitch(this._pageContext, currentWikiUrl, true);
            } catch (error) {
                this.handleEmbedWorkItemFailure(errorMessage);
            }
        }
    };

    public moveDownShortcutPressed = (): void => {
        this.treeAC.moveTreeItemOneLevel(false);
    };

    public moveUpShortcutPressed = (): void => {
        this.treeAC.moveTreeItemOneLevel(true);
    };

    public printCurrentPage = (): void => {
        document.body.dispatchEvent(new CustomEvent(PrintPageClickedEvent, {}));
    };

    public unsetUnfollowPageOption = (): void => {
        this._overviewActions.unsetUnfollowPage.invoke();
    };

    public onFollowStatusRefreshed = (): void => {
        this._overviewActions.refreshFollowStatus.invoke(false);
    };

    public clearError = (): void => {
        this._overviewActions.onErrorChanged.invoke();
    };

    private _warnBeforePageSwitch(onLeavePage: () => void, skipUpdateUrl?: boolean) {
        const overviewState = this._storesHub.stores.overviewStore.getState();
        if (overviewState.isEditing && this._asyncEditorAC && overviewState.pageInfo) {
            this._asyncEditorAC.warnBeforePageSwitch(() => {
                this.closeEditor(skipUpdateUrl);
                onLeavePage();
            }, overviewState.tempPageInfo || getPageInfoWithName(overviewState.pageInfo));
        } else {
            onLeavePage();
        }
    }

    private _startEditingEmptyPage = (parentPath: string, zeroDay: boolean = false, initialPageTitle: string = "") => {
        this._warnBeforePageSwitch(() => {
            setWindowTitle(this._pageContext, Resources.WindowTitleForNewPage);
            this._logPageEditAndStartScenario(true, false);
            const newPageInfo: WikiPageInfoWithName = { pageName: initialPageTitle, pageId: undefined, content: "", version: "", parentPath };
            this._overviewActions.startEditingNewPage.invoke({
                zeroDay,
                newPageInfo
            });
            this._initializeEditor(newPageInfo, zeroDay);
        });
    };

    private _loadHeaderMetadata = (pagePath: string): void => {
        const currentPageInfo = this._storesHub.stores.overviewStore.getState().pageInfo;
        const currentPage = currentPageInfo && this._storesHub.getState().treeState.treeItemProvider.getItemFromTree(currentPageInfo.path);
        // dont load header metadata in case of folders or non conformant pages
        if (currentPage && (currentPage.isNonConformant || isFolderAssociatedPage(currentPage))) {
            this._pageMetadatAC.then(ac => {
                ac.clearPageMetadata();
            });
            return;
        }

        const repositoryId = this._wiki.repositoryId;
        const gitItemPath = getGitItemPath(pagePath, this._wiki.mappedPath);
        this._pageMetadatAC.then(ac => {
            ac.fetchPageMetaData(repositoryId, this._version, gitItemPath, pagePath, this._wiki.id);
        });
    };

    private _loadCommitsData = (pagePath: string): void => {
        const repositoryId = this._wiki.repositoryId;
        const gitItemPath = getGitItemPath(pagePath, this._wiki.mappedPath);

        this._pageMetadatAC.then(ac => {
            ac.fetchLastCommitData(repositoryId, this._version, gitItemPath);
        });
    };

    private _onPageSelected = (newPage?: WikiPage, updateTree?: boolean, skipUpdateUrl?: boolean, anchor?: string): void => {
        this.treeAC.onPageSelected(newPage, updateTree, skipUpdateUrl, anchor);
    };

    private _initializeEditor = (editPageInfo: WikiPageInfoWithName, zeroDay?: boolean): void => {
        this._registerEditorFlux(editPageInfo, zeroDay).then(legacyScripts => {
            if (editPageInfo) {
                legacyScripts.wikiAutoCompleteNwpWrapperModule
                    .translateStorageKeysToDisplayNamesOfPersonMentions(editPageInfo.content)
                    .then(content => this._overviewActions.contentTranslatedForEditor.invoke(content));
            }
        });
    };

    private _getParentPageOrHomePagePath(pagePath: string): string | undefined {
        const parentPage = getParentPath(pagePath);

        return parentPage !== RepoConstants.RootPath ? parentPage : this._getHomePagePath();
    }

    private _getHomePagePath(): string | undefined {
        // todo: return homePagePath once implemented

        return undefined;
    }

    private _getNextPageToSelect(candidatePagePath: string | undefined): WikiPage | undefined {
        let newPagePath = candidatePagePath;
        const canNavigateToPage = candidatePagePath ? this._canNavigateToPage(candidatePagePath) : false;

        if (!canNavigateToPage) {
            const defaultPageToNavigateTo = this._getDefaultPageToNavigateTo();

            if (defaultPageToNavigateTo && defaultPageToNavigateTo.path) {
                // If we cannot operate on the given page path, navigate to the default page
                newPagePath = defaultPageToNavigateTo.path;
            } else {
                return undefined;
            }
        }

        return newPagePath ? this._storesHub.getState().treeState.treeItemProvider.getItemFromTree(newPagePath) : undefined;
    }

    private _canNavigateToPage(pagePath: string): boolean {
        const page = this._storesHub.stores.treeStore.getState().treeItemProvider.getItemFromTree(pagePath);
        const isPathNonRoot = pagePath !== RepoConstants.RootPath;
        const isPageNotLoaded = !page;
        const isPageLoadedAndConformant = page && !page.isNonConformant ? !page.isNonConformant : false;

        /**
         * To render or navigate to the page,
         * 1. The path exists and should be non-root since there is no content to be navigated to or rendered
         * 2. Either page is not loaded yet, or if the page is loaded and conformant
         */
        return isPathNonRoot && (isPageNotLoaded || isPageLoadedAndConformant);
    }

    /**
     * Gets the first page from the root level which is conformant. If no page from root level is conformant, returns undefined.
     */
    private _getDefaultPageToNavigateTo = (): WikiPage | undefined => {
        const homePagePath = this._storesHub.stores.treeStore.getState().homePagePath;
        if (homePagePath) {
            return this._storesHub.stores.treeStore.getState().treeItemProvider.getItemFromTree(homePagePath);
        }

        return this._storesHub.stores.treeStore.getState().treeItemProvider.getFirstConformantChild(RepoConstants.RootPath);
    };

    private _updateSavedPage = (page: WikiPage, pageVersion: string): void => {
        const isPageAddInProgress = !!this._storesHub.getState().overviewState.tempPageInfo;
        if (isPageAddInProgress) {
            this._pageMetadatAC.then(ac => {
                ac.clearPageMetadata();
            });
        }
        this._loadCommitsData(page.path);
        const closeEditor = (this._storesHub.asyncEditorStore && this._storesHub.asyncEditorStore.getState().closeEditorAfterSave) || false;
        if (!isSimpleUrlEnabled(this._pageContext) || closeEditor) {
            updateUrl(
                this._pageContext,
                {
                    pageId: page.id,
                    pagePath: page.path,
                    _a: closeEditor ? ActionIds.View : ActionIds.Edit,
                    anchor: undefined
                },
                undefined,
                undefined,
                true
            );
        } else {
            // since we support simple URL only for view, force it to be path based template on other places like revision, edit..etc
            // else by default it would pick simple URL format which doesn't have page path and so doesn't work
            this._updateToPagePathBasedUrlIfNeeded({ pagePath: page.path, pageId: page.id, _a: ActionIds.Edit }, false);
        }

        const oldPageInfo = this._storesHub.getState().overviewState.pageInfo;
        const oldPagePath = isPageAddInProgress || !oldPageInfo ? "" : oldPageInfo.path;
        this._overviewActions.pageSaved.invoke({
            page,
            pageVersion,
            oldPagePath,
            closeEditor
        });

        if (isPageAddInProgress) {
            this.treeAC.onFilteringDataSetChanged();
        } else if (oldPagePath !== page.path) {
            // rename
            this.treeAC.reloadPage(page.path);
            this.treeAC.onFilteringDataSetChanged();
        }
    };

    private _registerEditorFlux(pageInfoWithName: WikiPageInfoWithName, zeroDay?: boolean): Promise<IEditorLegacyModules> {
        if (this._registerEditorFluxPromise) {
            return this._registerEditorFluxPromise;
        }

        const legacyEditorScripts = this._pageContext
            .getService<IVssContributionService>("IVssContributionService")
            .getContributionAsync("ms.vss-features.markdown-toolbar-table-content")
            .then(() => {
                const legacyPlatformService = this._pageContext.getService<ILegacyPlatformService>("ILegacyPlatformService");
                return legacyPlatformService.requireModules(["Wiki/Scripts/WikiMentionNwpWrapper", "Wiki/Scenarios/Overview/Components/WikiToolbar"]);
            });

        const editorServicePromise = this._pageContext
            .getService<IVssContributionService>("IVssContributionService")
            .getService<IWikiEditorService>("ms.vss-wiki-web.wiki-editor-service");

        this._registerEditorFluxPromise = Promise.all([legacyEditorScripts, editorServicePromise]).then(
            (values: [[IWikiMentionNwpWrapperModule], IWikiEditorService]) => {
                const legacyScripts: IEditorLegacyModules = {
                    wikiAutoCompleteNwpWrapperModule: values[0][0]
                };
                const editorService = values[1];

                const { actionCreator, store, removeActionListeners } = editorService.setupFlux(
                    this.closeEditor,
                    pageInfoWithName.content,
                    this._updateSavedPage,
                    pageInfoWithName.pageName,
                    legacyScripts,
                    this._wiki.type,
                    zeroDay
                );

                this._asyncEditorAC = actionCreator;
                this._storesHub.registerEditorStore(store, removeActionListeners);

                this._addStoreListener(store);

                this._overviewActions.editorModuleLoaded.invoke(undefined);

                return legacyScripts;
            }
        );
        return this._registerEditorFluxPromise;
    }

    private get _pageMetadatAC(): Promise<IHeaderMetaDataActionCreator> {
        if (this._asyncPageMetaDataAC) {
            return Promise.resolve(this._asyncPageMetaDataAC);
        } else {
            return this._registerHeaderMetadataFlux();
        }
    }

    private _registerHeaderMetadataFlux(): Promise<IHeaderMetaDataActionCreator> {
        return this._pageContext
            .getService<IVssContributionService>("IVssContributionService")
            .getService<IHeaderMetaDataService>("ms.vss-wiki-web.wiki-headermetadata-service")
            .then((headerMetadatServiceService: IHeaderMetaDataService) => {
                const { actionCreator, store, removeActionListeners } = headerMetadatServiceService.setupFlux();
                this._asyncPageMetaDataAC = actionCreator;
                this._storesHub.registerHeaderMetaDataStore(store, removeActionListeners);
                this._addStoreListener(store);
                return actionCreator;
            });
    }

    private _logPageEditAndStartScenario = (isNewPage: boolean, isPageContentAlreadyLoaded: boolean) => {
        publish(this._pageContext, TelemetryConstants.PageEditingStarted, this._wiki.type, { isSubPage: isNewPage });

        const performanceService = this._pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        const props: { [key: string]: any } = {};
        props[TelemetryConstants.IsFirstTimeLoad] = this._registerEditorFluxPromise == undefined;
        props[TelemetryConstants.IsNewPage] = isNewPage;
        props[TelemetryConstants.RequiresFetchingPageData] = !isNewPage && !isPageContentAlreadyLoaded;
        performanceService.startScenario(PerformanceConstants.Editor, false, undefined, props);
    };

    private _updateUrlIfNeeded = (urlParameters: UrlParameters, replaceState?: boolean, skipUpdateUrl?: boolean): void => {
        if (!skipUpdateUrl) {
            updateUrl(this._pageContext, urlParameters, undefined, undefined, replaceState);
        }
    };

    private _updateToPagePathBasedUrlIfNeeded = (urlParameters: UrlParameters, replaceState?: boolean, skipUpdateUrl?: boolean): void => {
        if (!skipUpdateUrl) {
            updateToPagePathBasedUrlIfNeeded(
                this._pageContext,
                urlParameters,
                { wikiIdentifier: this._wiki.name, wikiVersion: getVersionFromBranchName(this._version.version) },
                replaceState
            );
        }
    };
}
